<template>
  <v-card min-height="420px" :loading="loading" :disabled="loading">
      <v-row justify="space-between">
        <v-btn v-if="!isAuctionStarted" style="margin-left:5px" @click.stop="" small color="error" elevation="3" v-text="$t('label.commingSoon')">
        </v-btn>
        <v-spacer v-else></v-spacer>
        <v-btn @click.stop="selectItem('chat')" :title="$t('label.messages')" small color="success" fab elevation="3">
          <v-icon small>mdi-message</v-icon>
        </v-btn>
      </v-row>
      <v-row justify="center">
        <v-img v-once :src="auctionPicture" max-width="170px" min-width="170px" max-height="100px" min-height="100px"></v-img>
      </v-row>
      <br>
    <v-card-text>
      <v-row justify="center">
        <h5>#{{ auction.id }}</h5>
      </v-row>
      <v-row justify="center">
        <h2 :title="auction.title">{{ cutText(auction.title, 20) }}</h2>
      </v-row>
      <v-row justify="center">
        <h4 color="primary"><a @click.stop="selectItem('detail')">{{ $t('label.moreDetail') }}</a></h4>
      </v-row>
      <br>
      <v-row justify="center">
        <div style="text-align: center">
          {{ finishOrStartText }} <strong>{{ finishOrStartValue }}</strong>
        </div>
      </v-row>
      <br>
      <v-row justify="center">
        <div>
          {{ priceText }} <strong>${{ auction.currentPrice }} {{ auction.currencyType.code }}</strong>
          <br>
          <span v-if="canBid">
            {{ $t('label.myBid') }} <strong>{{ myLastBid }}</strong>
          </span>
        </div>
      </v-row>
    </v-card-text>
    <v-card-actions>
      <v-row justify="center" v-if="canBid">
        <v-btn small color="primary" width="100"  @click.stop="selectItem('bid')">{{ $t('label.bid') }}</v-btn>
      </v-row>
      <v-row justify="center" v-if="needsAccept">
        <v-btn small color="error" width="100" @click.stop="selectItem('reject')">{{ $t('label.reject') }}</v-btn>
        <v-btn small color="primary" style="margin-left:10px" width="100" @click.stop="selectItem('accept')">{{ $t('label.accept') }}</v-btn>
      </v-row>
    </v-card-actions>
  </v-card>
</template>

<script>
import status from '../../../core/Auctions/status'
import { extractDateTime, formatDateTime, cutText } from '../../../core/sharedFunctions'

export default {
  name: 'AuctionCard',
  props: {
    auction: Object,
    loading: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    time: '00:00:00',
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    canBidCounter: true,
    intervalFunctionId: null
  }),
  computed: {
    isAuctionStarted () {
      const currentDate = new Date()
      return currentDate > new Date(extractDateTime(this.auction.startDateTime))
    },
    isAuctionFinished () {
      const currentDate = new Date()
      return currentDate > this.finishDateTime
    },
    finishOrStartText () {
      if (this.auction.statusId === status.EVALUATION_PENDING) {
        return this.$t('label.requiredDate')
      }
      if (this.isAuctionStarted) {
        if (this.isAuctionFinished) {
          return this.$t('label.finishedOn')
        } else {
          return this.$t('label.finishIn')
        }
      } else {
        return this.$t('label.startOn')
      }
    },
    finishOrStartValue () {
      if (this.auction.statusId === status.EVALUATION_PENDING) {
        return this.auction.requiredOn
      }
      if (this.isAuctionStarted) {
        if (this.isAuctionFinished) {
          return this.auction.finishOn
        } else {
          return this.time
        }
      } else {
        return this.auction.startOn
      }
    },
    priceText () {
      if (this.auction.statusId === status.EVALUATION_PENDING) {
        return this.$t('label.price')
      } else {
        return this.$t('label.currentPrice')
      }
    },
    canBid () {
      return this.isAuctionStarted && this.auction.statusId === status.OPEN && this.canBidCounter
    },
    needsAccept () {
      return this.auction.statusId === status.ACCEPTANCE_PENDING
    },
    myLastBid () {
      if (this.auction.bids.length > 0) {
        return `$${this.auction.bids[this.auction.bids.length - 1].bid} ${this.auction.currencyType.code}`
      }
      return '-'
    },
    finishDateTime () {
      const date = new Date(extractDateTime(this.auction.startDateTime))
      date.setTime(date.getTime() + (this.auction.hoursToExpire * 60 * 60 * 1000))
      return date
    },
    auctionPicture () {
      if (this.auction.pictureBase64) {
        return `data:image/*;base64,${this.auction.pictureBase64}`
      }
      return require('@/assets/Auctions/genericTruck.png')
    }
  },
  methods: {
    cutText (text, length) {
      return cutText(text, length)
    },
    setTimeVariables () {
      const finishDate = new Date(this.auction.finishOn)
      const currentDate = new Date()

      let totalSeconds = Math.floor(Math.abs(finishDate - currentDate) / 1000)

      this.days = Math.floor(totalSeconds / 86400)
      totalSeconds -= this.days * 86400
      this.hours = Math.floor(totalSeconds / 3600)
      totalSeconds -= this.hours * 3600
      this.minutes = Math.floor(totalSeconds / 60)
      totalSeconds -= this.minutes * 60
      this.seconds = Math.floor(totalSeconds)
      this.time = `${this.hours}:${this.minutes}:${this.seconds}`
      this.intervalFunctionId = setInterval(() => this.countDown(), 1000)
    },
    countDown () {
      if (this.canBid) {
        this.seconds--
        if (this.seconds < 0) {
          this.seconds = 59
          this.minutes--
          if (this.minutes < 0) {
            this.minutes = 59
            this.hours--
            if (this.hours < 0) {
              this.hours = 23
              this.days--
              if (this.days < 0) {
                this.days = 0
                this.hours = 0
                this.minutes = 0
                this.seconds = 0
                this.canBidCounter = false
                window.clearInterval(this.intervalFunctionId)
              }
            }
          }
        }
        this.time = `${this.$tc('label.days', this.days, { n: this.days })}
          ${this.$tc('label.hours', this.hours, { n: this.hours.toString().padStart(2, '0') })}
          ${this.$tc('label.minutes', this.minutes, { n: this.minutes.toString().padStart(2, '0') })}
          ${this.$tc('label.seconds', this.seconds, { n: this.seconds.toString().padStart(2, '0') })}`
      }
    },
    selectItem (action) {
      this.$emit('itemSelected', this.auction, action)
    },
    setDateTimes () {
      // Set finish on date
      this.$set(this.auction, 'finishOn', formatDateTime(this.finishDateTime))
      if (this.canBid) {
        this.setTimeVariables()
      }
      // Set start on date
      this.$set(this.auction, 'startOn', formatDateTime(new Date(extractDateTime(this.auction.startDateTime))))
      // Set required date
      this.$set(this.auction, 'requiredOn', formatDateTime(new Date(extractDateTime(this.auction.requiredDateTime))))
    }
  },
  mounted () {
    this.setDateTimes()
  }
}
</script>

<style>

</style>
